import React, { useState, useEffect } from "react";
import { CheckCircle, Copy, Trash2, Smartphone, MapPin, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { affiliateApi } from "../services/influancerService";
import Swal from "sweetalert2";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import LoadingSpinner from "../../components/LoadingSpinner";
import { INFLUENCER_BASE } from "../constants";

const ShareLinks = () => {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(null);
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [selectedLink, setSelectedLink] = useState(null);

    useEffect(() => {
        fetchAffiliateLinks();
    }, []);

    const fetchAffiliateLinks = async () => {
        try {
            setLoading(true);
            const response = await affiliateApi.getLinks();
            setLinks(response);
            setError(null);
        } catch (err) {
            setError("Failed to load affiliate links");
            console.error("Error fetching links:", err);
            Swal.fire({ icon: "error", title: "Error", text: "Failed to load affiliate links. Please try again later." });
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (url, id) => {
        navigator.clipboard.writeText(url);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
        Swal.fire({ icon: "success", title: "Copied!", text: "Link copied to clipboard", timer: 1500, showConfirmButton: false });
    };

    const handleLinkClick = async (link) => {
        try {
            const shortCode = link.generatedLink.split("/a/")[1];
            const fp = await FingerprintJS.load();
            const result = await fp.get();
            const deviceFingerprint = result.visitorId;
            const clickTimestamp = Date.now().toString();

            const storeWithExpiry = (key, value) => {
                const expiryDate = new Date();
                expiryDate.setDate(expiryDate.getDate() + 30);
                localStorage.setItem(key, value);
                localStorage.setItem(`${key}_expiry`, expiryDate.getTime());
            };

            storeWithExpiry("shortCode", shortCode);
            storeWithExpiry("deviceFingerprint", deviceFingerprint);
            storeWithExpiry("clickTimestamp", clickTimestamp);

            const cookieOptions = "max-age=2592000; path=/; SameSite=None; Secure";
            document.cookie = `shortCode=${shortCode}; ${cookieOptions}`;
            document.cookie = `deviceFingerprint=${deviceFingerprint}; ${cookieOptions}`;
            document.cookie = `clickTimestamp=${clickTimestamp}; ${cookieOptions}`;

            const response = await affiliateApi.handleLinkClick(shortCode, deviceFingerprint);

            if (response.clickId) {
                storeWithExpiry("lastClickId", response.clickId);
                document.cookie = `lastClickId=${response.clickId}; ${cookieOptions}`;
            }

            const params = new URLSearchParams({ lastClickId: response.clickId, deviceFingerprint, shortCode, clickTimestamp });
            const baseUrl = response.actualLink || "";
            const redirectUrl = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${params.toString()}`;
            window.open(redirectUrl, "_blank");

            await fetchAffiliateLinks();

            if (response.isUnique) {
                Swal.fire({ icon: "success", title: "Unique Visit!", text: "This is a new unique visit to your affiliate link.", timer: 2000, showConfirmButton: false });
            }
        } catch (error) {
            console.error("Error handling link click:", error);
            Swal.fire({ icon: "error", title: "Error", text: error.message || "Failed to process link. Please try again." });
        }
    };

    const formatRemainingTime = (days) => {
        if (days === 0) return "Expired";
        return `${days} days remaining`;
    };

    const getLinkStatus = (link) => {
        if (link.status === "Deactive" || link.remainingTime === 0) return "expired";
        if (link.remainingTime <= 5) return "expiring-soon";
        return "active";
    };

    const handleDelete = async (linkId) => {
        try {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#ef4444",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                await affiliateApi.deleteLink(linkId);
                setLinks(links.filter((link) => link._id !== linkId));
                Swal.fire("Deleted!", "Your link has been deleted.", "success");
            }
        } catch (error) {
            console.error("Error deleting link:", error);
            Swal.fire({ icon: "error", title: "Error", text: "Failed to delete link. Please try again." });
        }
    };

    const handleShowDeviceDetails = (link) => { setSelectedLink(link); setShowDeviceModal(true); };
    const handleShowLocationDetails = (link) => { setSelectedLink(link); setShowLocationModal(true); };
    const closeModals = () => { setShowDeviceModal(false); setShowLocationModal(false); setSelectedLink(null); };

    const getDeviceDetails = (link) => {
        if (!link.uniqueClicks?.length) return [];
        return link.uniqueClicks.map((click, index) => ({
            id: index,
            os: click.device?.os || "Unknown",
            osVersion: click.device?.osVersion || "",
            browser: click.device?.browser || "Unknown",
            browserVersion: click.device?.browserVersion || "",
            deviceType: click.device?.deviceType || "Desktop",
            timestamp: click.timestamp,
        }));
    };

    const getLocationDetails = (link) => {
        if (!link.uniqueClicks?.length) return [];
        return link.uniqueClicks.map((click, index) => ({
            id: index,
            city: click.location?.city || "Unknown",
            region: click.location?.region || "Unknown",
            country: click.location?.country || "Unknown",
            ip: click.location?.ip || "Unknown",
            timestamp: click.timestamp,
        }));
    };

    const formatDate = (dateStr) =>
        new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(dateStr));

    return (
        <div className="w-full p-4 lg:p-6">
            {/* Back */}
            <button
                onClick={() => navigate(-1)}
                className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600 lg:mb-4"
            >
                <FaArrowLeft className="h-3.5 w-3.5" />
                Back
            </button>

            {/* Header */}
            <div className="mb-4 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 md:flex-row md:items-center md:justify-between lg:mb-6">
                <div className="min-w-0 flex-1">
                    <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">Your Affiliate Links</h1>
                    <p className="mt-1 text-sm text-gray-500">View, copy, and track all your generated affiliate links.</p>
                </div>
                <button
                    onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/generate-link`)}
                    className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
                >
                    + Generate New Link
                </button>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex min-h-[200px] items-center justify-center">
                    <LoadingSpinner message="Loading your links..." size="medium" />
                </div>
            ) : error ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">{error}</div>
            ) : links.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm">
                    <p className="text-sm text-gray-500">No affiliate links generated yet.</p>
                    <button
                        onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/generate-link`)}
                        className="mt-4 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
                    >
                        Generate New Link
                    </button>
                </div>
            ) : (
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="divide-y divide-gray-100">
                        {links.map((link) => {
                            const status = getLinkStatus(link);
                            return (
                                <div key={link._id} className="p-4 transition hover:bg-gray-50 sm:p-5">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                        {/* Left info */}
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-medium text-gray-800" title={link.actualLink}>
                                                {link.actualLink}
                                            </p>
                                            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                                                <span>Created: {formatDate(link.createdAt)}</span>
                                                <span className="font-medium text-blue-600">Total Visits: {link.visits || 0}</span>
                                                <span className="font-medium text-purple-600">Unique Visits: {link.uniqueVisits || 0}</span>
                                                <span className="font-medium text-orange-500">Conversions: {link.conversions || 0}</span>
                                                {link.visits > 0 && (
                                                    <span className="font-medium text-green-600">
                                                        CR: {((link.conversions / link.visits) * 100).toFixed(1)}%
                                                    </span>
                                                )}
                                                <span className={`font-semibold ${
                                                    status === "expired" ? "text-red-500" : status === "expiring-soon" ? "text-yellow-600" : "text-green-600"
                                                }`}>
                                                    {formatRemainingTime(link.remainingTime)}
                                                </span>
                                            </div>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => handleShowDeviceDetails(link)}
                                                    className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600 transition hover:bg-blue-100"
                                                >
                                                    <Smartphone className="h-3 w-3" />
                                                    Devices ({link.uniqueClicks?.length || 0})
                                                </button>
                                                <button
                                                    onClick={() => handleShowLocationDetails(link)}
                                                    className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-600 transition hover:bg-green-100"
                                                >
                                                    <MapPin className="h-3 w-3" />
                                                    Locations ({link.uniqueClicks?.length || 0})
                                                </button>
                                            </div>
                                        </div>

                                        {/* Right: link + action */}
                                        <div className="flex shrink-0 items-center gap-2">
                                            <a
                                                href={link.generatedLink}
                                                onClick={(e) => { e.preventDefault(); handleLinkClick(link); }}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`max-w-[160px] truncate text-xs sm:max-w-[200px] sm:text-sm ${
                                                    status === "expired" ? "text-gray-400 line-through" : "text-blue-600 hover:underline"
                                                }`}
                                                title={link.generatedLink}
                                            >
                                                {link.generatedLink}
                                            </a>
                                            {status !== "expired" ? (
                                                <button
                                                    className="rounded-lg border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-100"
                                                    onClick={() => handleCopy(link.generatedLink, link._id)}
                                                    title="Copy link"
                                                >
                                                    {copied === link._id ? (
                                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                                    ) : (
                                                        <Copy className="h-4 w-4" />
                                                    )}
                                                </button>
                                            ) : (
                                                <button
                                                    className="rounded-lg border border-red-200 bg-red-50 p-1.5 text-red-500 transition hover:bg-red-100"
                                                    onClick={() => handleDelete(link._id)}
                                                    title="Delete expired link"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Device Details Modal */}
            {showDeviceModal && selectedLink && (
                <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4">
                    <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4">
                            <h3 className="flex items-center gap-2 text-base font-bold text-gray-900">
                                <Smartphone className="h-4 w-4 text-blue-500" />
                                Device Details
                            </h3>
                            <button onClick={closeModals} className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-5">
                            <p className="mb-3 text-xs text-gray-500">
                                Link: <span className="font-medium text-blue-600">{selectedLink.generatedLink}</span>
                            </p>
                            <p className="mb-4 text-xs text-gray-500">
                                Total Unique Visitors: <span className="font-bold text-gray-800">{selectedLink.uniqueClicks?.length || 0}</span>
                            </p>
                            {getDeviceDetails(selectedLink).length === 0 ? (
                                <p className="py-8 text-center text-sm text-gray-400">No device data available yet</p>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {["#", "Device Type", "Operating System", "Browser", "Visit Time"].map(h => (
                                                    <th key={h} className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold text-gray-500">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {getDeviceDetails(selectedLink).map((device, index) => (
                                                <tr key={device.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-2.5 text-xs text-gray-500">{index + 1}</td>
                                                    <td className="px-4 py-2.5">
                                                        <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{device.deviceType}</span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-700">
                                                        {device.os}{device.osVersion && <span className="text-gray-400"> v{device.osVersion}</span>}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-700">
                                                        {device.browser}{device.browserVersion && <span className="text-gray-400"> v{device.browserVersion}</span>}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-500">
                                                        {device.timestamp ? new Date(device.timestamp).toLocaleString() : "N/A"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Location Details Modal */}
            {showLocationModal && selectedLink && (
                <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4">
                    <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4">
                            <h3 className="flex items-center gap-2 text-base font-bold text-gray-900">
                                <MapPin className="h-4 w-4 text-green-500" />
                                Location Details
                            </h3>
                            <button onClick={closeModals} className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-5">
                            <p className="mb-3 text-xs text-gray-500">
                                Link: <span className="font-medium text-blue-600">{selectedLink.generatedLink}</span>
                            </p>
                            <p className="mb-4 text-xs text-gray-500">
                                Total Unique Visitors: <span className="font-bold text-gray-800">{selectedLink.uniqueClicks?.length || 0}</span>
                            </p>
                            {getLocationDetails(selectedLink).length === 0 ? (
                                <p className="py-8 text-center text-sm text-gray-400">No location data available yet</p>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {["#", "Country", "Region", "City", "IP Address", "Visit Time"].map(h => (
                                                    <th key={h} className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold text-gray-500">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                            {getLocationDetails(selectedLink).map((location, index) => (
                                                <tr key={location.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-2.5 text-xs text-gray-500">{index + 1}</td>
                                                    <td className="px-4 py-2.5">
                                                        <span className="rounded-md bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">{location.country}</span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-700">{location.region}</td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-700">{location.city}</td>
                                                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{location.ip}</td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-500">
                                                        {location.timestamp ? new Date(location.timestamp).toLocaleString() : "N/A"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShareLinks;
